import type { SearchFilterState } from '../../types'
import { formatDate, parseDate } from './helpers'

const splitComma = (raw: string) => raw.split(',').filter(Boolean)

/** Defines how a single criteria segment is serialised to and from a {@link SearchFilterState}. */
export type SegmentDef = {
  /** Returns the current state value for encoding. */
  get: (state: SearchFilterState) => null | boolean | string | string[]
  /** Writes a raw criteria value back into state for decoding. */
  set: (raw: string, state: SearchFilterState) => void
}

/**
 * A static, label-keyed map that is the single source of truth for every criteria segment.
 * `get` is used by the encoder; `set` is used by the decoder.
 * Special cases (Status, Provider, MediaType, Industries) are handled separately.
 */
export const SEGMENT_MAP: Record<string, SegmentDef> = {
  Application: {
    get: (state) => state.productInformation.applications,
    set: (raw, state) => {
      state.productInformation.applications = splitComma(raw)
    },
  },
  Category: {
    get: (state) => state.documentInformation.categories,
    set: (raw, state) => {
      state.documentInformation.categories = splitComma(raw)
    },
  },
  Duty: {
    get: (state) => state.productInformation.duties,
    set: (raw, state) => {
      state.productInformation.duties = splitComma(raw)
    },
  },
  'Emission Tier': {
    get: (state) => state.productInformation.emissionTiers,
    set: (raw, state) => {
      state.productInformation.emissionTiers = splitComma(raw)
    },
  },
  'Engine Model': {
    get: (state) => state.productInformation.engineModels,
    set: (raw, state) => {
      state.productInformation.engineModels = splitComma(raw)
    },
  },
  Frequency: {
    get: (state) => state.productInformation.frequencies,
    set: (raw, state) => {
      state.productInformation.frequencies = splitComma(raw)
    },
  },
  'Fuel Type': {
    get: (state) => state.productInformation.fuelTypes,
    set: (raw, state) => {
      state.productInformation.fuelTypes = splitComma(raw)
    },
  },
  Language: {
    get: (state) => state.mediaDetails.languages,
    set: (raw, state) => {
      state.mediaDetails.languages = splitComma(raw)
    },
  },
  ProductID: {
    get: (state) => state.mediaDetails.productId,
    set: (raw, state) => {
      state.mediaDetails.productId = raw
    },
  },
  'Product Type': {
    get: (state) => state.documentInformation.productTypes,
    set: (raw, state) => {
      state.documentInformation.productTypes = splitComma(raw)
    },
  },
  RPM: {
    get: (state) => state.productInformation.rpms,
    set: (raw, state) => {
      state.productInformation.rpms = splitComma(raw)
    },
  },
  Source: {
    get: (state) => state.productInformation.sources,
    set: (raw, state) => {
      state.productInformation.sources = splitComma(raw)
    },
  },
  SubIndustry: {
    get: (state) => state.documentInformation.subIndustries,
    set: (raw, state) => {
      state.documentInformation.subIndustries = splitComma(raw)
    },
  },
  Voltage: {
    get: (state) => state.productInformation.voltages,
    set: (raw, state) => {
      state.productInformation.voltages = splitComma(raw)
    },
  },
  Keyword: {
    get: (state) => state.advancedTextSearching.keyword,
    set: (raw, state) => {
      state.advancedTextSearching.keyword = raw
    },
  },
  'Long Description': {
    get: (state) => state.advancedTextSearching.longDescription,
    set: (raw, state) => {
      state.advancedTextSearching.longDescription = raw
    },
  },
  Title: {
    get: (state) => state.advancedTextSearching.title,
    set: (raw, state) => {
      state.advancedTextSearching.title = raw
    },
  },
  'Last Modified From': {
    get: (state) => formatDate(state.mediaDetails.modifiedFrom),
    set: (raw, state) => {
      state.mediaDetails.modifiedFrom = parseDate(raw)
    },
  },
  'Last Modified To': {
    get: (state) => formatDate(state.mediaDetails.modifiedTo),
    set: (raw, state) => {
      state.mediaDetails.modifiedTo = parseDate(raw)
    },
  },
  'Secured From': {
    get: (state) => formatDate(state.mediaDetails.securedFrom),
    set: (raw, state) => {
      state.mediaDetails.securedFrom = parseDate(raw)
    },
  },
  To: {
    get: (state) => formatDate(state.mediaDetails.securedTo),
    set: (raw, state) => {
      state.mediaDetails.securedTo = parseDate(raw)
    },
  },
  // Backwards-compat alias: the legacy encoder emitted " To" (with a leading space).
  // This entry decodes existing saved searches / URLs that use the old label.
  // `get` returns null so the encoder never emits this key.
  ' To': {
    get: () => null,
    set: (raw, state) => {
      state.mediaDetails.securedTo = parseDate(raw)
    },
  },
  'Media Number': {
    get: (state) => state.documentInformation.mediaNumber,
    set: (raw, state) => {
      state.documentInformation.mediaNumber = raw
    },
  },
  'Performance No': {
    get: (state) => state.productInformation.performanceNo,
    set: (raw, state) => {
      state.productInformation.performanceNo = raw
    },
  },
  'Revision No': {
    get: (state) => state.productInformation.revisionNo,
    set: (raw, state) => {
      state.productInformation.revisionNo = raw
    },
  },
  AffCode: {
    get: (state) => state.mediaDetails.affiliationCode,
    set: (raw, state) => {
      state.mediaDetails.affiliationCode = raw
    },
  },
  'Rating kVA': {
    get: (state) => state.productInformation.ratings.kva,
    set: (raw, state) => {
      state.productInformation.ratings.kva = raw
    },
  },
  'Rating bKW': {
    get: (state) => state.productInformation.ratings.bkw,
    set: (raw, state) => {
      state.productInformation.ratings.bkw = raw
    },
  },
  'Rating bhp': {
    get: (state) => state.productInformation.ratings.bhp,
    set: (raw, state) => {
      state.productInformation.ratings.bhp = raw
    },
  },
  'Rating eKW': {
    get: (state) => state.productInformation.ratings.ekw,
    set: (raw, state) => {
      state.productInformation.ratings.ekw = raw
    },
  },
  'Rating mhp': {
    get: (state) => state.productInformation.ratings.mhp,
    set: (raw, state) => {
      state.productInformation.ratings.mhp = raw
    },
  },
  'Country of Use': {
    get: (state) => state.productInformation.countryOfUse.map((i) => i.label),
    set: (raw, state) => {
      state.productInformation.countryOfUse = splitComma(raw).map((l) => ({
        label: l,
        valueCode: '',
      }))
    },
  },
  'Gas Engine Rating Pro (GERP)': {
    get: (state) => state.productInformation.GERP,
    set: (raw, state) => {
      state.productInformation.GERP = raw === 'Yes'
    },
  },
  CSQ: {
    get: (state) => state.productInformation.CSQ,
    set: (raw, state) => {
      state.productInformation.CSQ = raw === 'Yes'
    },
  },
  'Configurable Sales Model': {
    get: (state) => state.productInformation.configurableSalesModel,
    set: (raw, state) => {
      state.productInformation.configurableSalesModel = raw
    },
  },
  'Power.Cat.Com': {
    get: (state) => state.productInformation.powerDotCatDotCom,
    set: (raw, state) => {
      state.productInformation.powerDotCatDotCom = raw === 'Yes'
    },
  },
  'Technical Information Portal (TIP)': {
    get: (state) => state.productInformation.TIP,
    set: (raw, state) => {
      state.productInformation.TIP = raw === 'Yes'
    },
  },
  Excitation: {
    get: (state) => state.productInformation.excitation.map((i) => i.label),
    set: (raw, state) => {
      state.productInformation.excitation = splitComma(raw).map((l) => ({
        label: l,
        valueCode: '',
      }))
    },
  },
  'Electric Power Spec Sizer': {
    get: (state) => state.productInformation.electricPowerSpecSizer,
    set: (raw, state) => {
      state.productInformation.electricPowerSpecSizer = raw === 'Yes'
    },
  },
  'CAT.com Product Technical Information': {
    get: (state) =>
      state.productInformation.catDotComProductTechnicalInformation,
    set: (raw, state) => {
      state.productInformation.catDotComProductTechnicalInformation =
        raw === 'Yes'
    },
  },
  'Media Applies To': {
    get: (state) => state.productInformation.appliesTo,
    set: (raw, state) => {
      state.productInformation.appliesTo = raw
    },
  },
  'Feature Code': {
    get: (state) => state.productInformation.featureCode,
    set: (raw, state) => {
      state.productInformation.featureCode = raw
    },
  },
  'Generator Arrangement': {
    get: (state) => state.productInformation.generatorArrangement,
    set: (raw, state) => {
      state.productInformation.generatorArrangement = raw
    },
  },
  'Target Region': {
    get: (state) => state.productInformation.targetRegions,
    set: (raw, state) => {
      state.productInformation.targetRegions = splitComma(raw)
    },
  },
  TextSearch: {
    get: (state) => state.searchText,
    set: (raw, state) => {
      state.searchText = raw
    },
  },
}
